import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/shared/lib/cn';
import { useT } from '@/shared/i18n';
import type { MessageKey } from '@/shared/i18n/locales/en';

type State = 'placeholder' | 'loading' | 'error' | 'empty';

export interface PageStatusProps extends HTMLAttributes<HTMLElement> {
  state?: State;
  title?: string;
  body?: string;
  children?: ReactNode;
}

const labelKey: Record<State, MessageKey> = {
  placeholder: 'page-status.placeholder.label',
  loading: 'page-status.loading.label',
  error: 'page-status.error.label',
  empty: 'page-status.empty.label',
};

/**
 * Page-level status surface. Renders an accessible region with a title and body.
 * Designed to be the entire page contents for a placeholder / loading / error /
 * empty state. No animation; no decorative imagery.
 */
export const PageStatus = forwardRef<HTMLElement, PageStatusProps>(function PageStatus(
  { state = 'placeholder', title, body, className, children, ...rest },
  ref,
) {
  const { t } = useT();
  const ariaLabel = t(labelKey[state]);

  return (
    <section
      ref={ref}
      role="status"
      aria-label={ariaLabel}
      aria-live={state === 'loading' ? 'polite' : 'off'}
      data-state={state}
      className={cn(
        'flex flex-col items-center justify-center gap-4 py-16 text-center',
        className,
      )}
      {...rest}
    >
      {title !== undefined ? (
        <h1 className="text-3xl font-semibold text-[var(--text-primary)]">{title}</h1>
      ) : null}
      {body !== undefined ? (
        <p className="max-w-prose text-[var(--text-secondary)]">{body}</p>
      ) : null}
      {children}
    </section>
  );
});
